import { Wrench } from "lucide-react";

// A maintenance-request post inside a house chat. Rendered distinctly from a
// normal message bubble (amber card) and shows who submitted it plus the linked
// task's CURRENT status — the status is resolved server-side on each poll, so
// this reflects status changes live. Shared by the host HouseChatPanel and the
// member MemberMessages surface so both look identical.
export interface HouseChatLinkedTask {
  id: number;
  title: string;
  status: string;
}

function statusMeta(status: string | null | undefined): { label: string; className: string } {
  switch ((status ?? "").toLowerCase()) {
    case "open":
      return { label: "Open", className: "border-amber-300 bg-amber-100 text-amber-800" };
    case "in_progress":
    case "in progress":
      return { label: "In progress", className: "border-blue-300 bg-blue-100 text-blue-800" };
    case "completed":
    case "done":
    case "closed":
      return { label: "Completed", className: "border-green-300 bg-green-100 text-green-800" };
    case "cancelled":
    case "canceled":
      return { label: "Cancelled", className: "border-gray-300 bg-gray-100 text-gray-600" };
    default:
      return { label: status || "Unknown", className: "border-gray-300 bg-gray-100 text-gray-700" };
  }
}

export function HouseChatMaintenanceCard({
  title,
  senderName,
  task,
}: {
  title: string;
  senderName: string;
  task: HouseChatLinkedTask | null;
}) {
  const st = statusMeta(task?.status);
  return (
    <div className="w-full rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="house-chat-maintenance-card">
      <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-amber-800">
        <Wrench className="h-3.5 w-3.5" /> Maintenance request
      </div>
      <p className="break-words text-sm font-medium text-gray-900">{title}</p>
      <div className="mt-1.5 flex items-center justify-between gap-2">
        <span className="min-w-0 truncate text-xs text-gray-500">Submitted by {senderName}</span>
        <span
          className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-medium ${st.className}`}
          data-testid="house-chat-maintenance-status"
        >
          {task ? st.label : "Removed"}
        </span>
      </div>
    </div>
  );
}
